import { Injectable } from '@angular/core'

import UuidUtility from './../core/uuid-utility'
import { NavItem, PrimaryNavItem, SecondaryNavItem } from './left-nav-item/nav-item'
import { NavItemLinkType } from './left-nav-item/nav-item-link-type'
import { NavComponentItem, NavComponentPrimaryItem, NavComponentSecondaryItem } from './left-nav-item/nav-component-item'
import { FooterLinkItem, NavComponentFooterItem } from './left-nav-footer-links/left-nav-footer-link'
import { SelectedNavItem } from './left-nav-item/selected-nav-item'
import { LEFT_NAV_ITEM_VALID_ATTRIBUTE_TYPES } from './left-nav-item/valid-attribute-types'

export const MAX_FOOTER_LINKS: number = 2

@Injectable({providedIn: 'root'})
export class LeftNavDataService {

  private navId: string
  public gaCategory: string

  constructor() {
    // noop
  }

  public getSelectedItem(navItems: NavComponentPrimaryItem[], selectedPath?: string): SelectedNavItem {

    for (const item of navItems) {

      if (this.itemIsSelected(item, selectedPath)) {
        return { itemId: item.domId }
      }

      if (item.secondaryItems) {
        for (const secondaryItem of item.secondaryItems) {
          if (this.itemIsSelected(secondaryItem, selectedPath)) {
            return { itemId: secondaryItem.domId, parentId: item.domId }
          }
        }
      }

    }

    return undefined

  }

  public getComponentItemsFromTree(tree: PrimaryNavItem[]): NavComponentPrimaryItem[] {
    return tree.map(this.getPrimaryItemFromTree.bind(this))
  }

  public getFooterComponentItems(items: FooterLinkItem[]): NavComponentFooterItem[] {
    // get the footer links, then only get the first few up to the MAX
    return items.map((item, index) => {
      const footerItem: NavComponentFooterItem = this.getPrimaryItemFromTree(item, index, 'footer')
      footerItem.action = item.action
      return footerItem
    }).slice(0, MAX_FOOTER_LINKS)
  }

  public getPrimaryItemFromTree(item: PrimaryNavItem, index: number, key?: string): NavComponentPrimaryItem {

    const hasSecondaryNav = this.hasSecondaryNavigation(item)
    const componentItem = this.getNavigationComponentItemWithLinkType(item)
    const primaryItem = {
      label: componentItem.label,
      gaLabel: undefined,
      domId: this.getDomId(item, index, key, true),
      indicatorDot: item.indicatorDot,
      tags: item.tags,
      href: componentItem.href,
      uiSref: componentItem.uiSref,
      routerLink: componentItem.routerLink,
      gotoContext: componentItem.gotoContext,
      secondaryItems: hasSecondaryNav ? this.getSecondaryComponentItemsFromTree(item, componentItem.gaLabel) : [],
      attributes: item.attributes,
      hasValidSecondaryItems: hasSecondaryNav,
      isSelected: item.isSelected
    }

    if (!hasSecondaryNav && componentItem.gaLabel) {
      primaryItem.gaLabel = componentItem.gaLabel
    }

    return primaryItem

  }

  public getNavId(): string {

    if (!this.navId) {
      this.initUuidForNav()
    }

    return this.navId

  }

  private initUuidForNav(): void {
    this.navId = UuidUtility.getUuid()
  }

  private getNavigationComponentItemWithLinkType(item: NavItem): NavComponentItem {

    const newItem: NavComponentItem = {
      label: item.label,
      domId: '',
      href: false,
      uiSref: false,
      routerLink: false,
      gotoContext: false
    }

    if (this.gaCategory) {
      newItem.gaLabel = this.getGaLabel(item)
    }

    if (this.hasSecondaryNavigation(item)) {
      return newItem
    }

    for (const type in NavItemLinkType) {
      newItem[type] = (item.attributes && item.attributes.hasOwnProperty(type))
    }

    return newItem

  }

  private getSecondaryItemFromTree(item: SecondaryNavItem, index: number, primaryGaLabel: string): NavComponentSecondaryItem {

    const componentItem = this.getNavigationComponentItemWithLinkType(item)
    const secondaryItem = {
      label: componentItem.label,
      gaLabel: undefined,
      domId: this.getDomId(item, index),
      indicatorDot: item.indicatorDot,
      tags: item.tags,
      href: componentItem.href,
      uiSref: componentItem.uiSref,
      routerLink: componentItem.routerLink,
      gotoContext: componentItem.gotoContext,
      attributes: item.attributes,
      isSelected: item.isSelected
    }

    if (componentItem.gaLabel) {
      secondaryItem.gaLabel = primaryGaLabel ? `${primaryGaLabel}.${componentItem.gaLabel}` : componentItem.gaLabel
    }

    return secondaryItem

  }

  private getSecondaryComponentItemsFromTree(item: PrimaryNavItem, primaryGaLabel: string): NavComponentSecondaryItem[] {
    return item.secondaryItems.map((item, index) => this.getSecondaryItemFromTree(item, index, primaryGaLabel))
  }

  private hasSecondaryNavigation(item: PrimaryNavItem): boolean {
    return (item.hasOwnProperty('secondaryItems') && item.secondaryItems.length > 0)
  }

  private getDomId(item: NavItem, index: number, key?: string, primary?: boolean): string {

    if (!this.navId) {
      this.initUuidForNav()
    }

    const primaryLabel = (primary) ? 'primary' : 'secondary'
    const keySubstring = (key) ? '-key' : ''
    const label = item.label.replace(/\s+/g, '-').toLowerCase()
    return `se-ui-left-nav--${this.navId}__${primaryLabel}-item--${label}${keySubstring}-${index}`

  }

  private getGaLabel(item: NavItem): string {
    // code from js-pascalcase: https://github.com/huynhsamha/js-pascalcase/blob/master/index.js
    return item.gaLabel || String(item.label)
      .replace(/^[^A-Za-z0-9]*|[^A-Za-z0-9]*$/g, '$')
      .replace(/[^A-Za-z0-9]+/g, '$')
      .replace(/([a-z])([A-Z])/g, (m, a, b) => `${a}$${b}`)
      .toLowerCase()
      .replace(/(\$)(\w?)/g, (m, a, b) => b.toUpperCase())
  }

  private itemIsSelected(item: NavComponentItem, selectedPath?: string): boolean {

    if (item.isSelected) {
      return true
    }

    for (const attributeType of LEFT_NAV_ITEM_VALID_ATTRIBUTE_TYPES) {
      if (item[attributeType] && item.attributes[attributeType] === selectedPath) {
        return true
      }
    }

    return false

  }

}
